import { renderGame, renderCharTiles, setCommentary } from '../ui/render.js';
import { CHAR_LABELS } from '../engine/constants.js';
import { jokerExchangeOptions } from '../engine/turn.js';
import { loadGameRow, submitMove } from '../net/api.js';
import { subscribeGame } from '../net/sync.js';
import { sortHand, tileName } from '../engine/tiles.js';
import { dingYourTurn } from '../ui/sound.js';
import { rankTargets, targetNeeds } from '../engine/patterns.js';
import { activateChat, deactivateChat } from './chat.js';

// Online-mode driver. State is pulled from Supabase; we never mutate it
// locally. Player actions call submit_move and trust the realtime update
// to re-render.

const TURN_ORDER = ['East', 'South', 'West', 'North'];

let state = null;
let seatIdx = -1;
let mySeat = '';
let gameId = '';
let ui = { selectedTile: -1, claimPending: false, charPicked: [], hintPattern: null, hintIdx: -1 };
let unsub = null;
let pollTimer = null;
let busy = false;
let onExitCallback = null;

export async function startOnlineGame({ gameId: id, seat, onExit }) {
  gameId = id;
  mySeat = seat;
  seatIdx = TURN_ORDER.indexOf(seat);
  onExitCallback = onExit;
  ui = { selectedTile: -1, claimPending: false, charPicked: [], hintPattern: null, hintIdx: -1 };
  busy = false;

  // Show the Chat button (hidden outside online mode).
  const chatBtn = document.getElementById('btnChatToggle');
  if (chatBtn) chatBtn.style.display = '';

  const game = await loadGameRow(gameId);
  if (game) applyServerState(game.state);

  // Hydrate chat after we have state so we know the display name.
  if (state) {
    const myName = state.players[seatIdx]?.name || seat;
    activateChat({
      gameId,
      seat: mySeat,
      displayName: myName,
      onUnread: (n) => {
        const badge = document.getElementById('chatBadge');
        if (!badge) return;
        if (n > 0) {
          badge.textContent = String(n);
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      },
    });
  }

  unsub = subscribeGame(gameId, {
    onGame: (row) => applyServerState(row.state),
  });

  // Fallback poll: if realtime is silent on this network/device, still
  // pick up opponent moves within a few seconds.
  pollTimer = setInterval(async () => {
    if (busy || !gameId) return;
    try {
      const row = await loadGameRow(gameId);
      if (row) applyServerState(row.state);
    } catch {
      /* transient — next tick will try again */
    }
  }, 5000);

  setCommentary('Online game — waiting for server update…');
}

let lastCurrentPlayer = -1;
let lastHandIds = new Set();
let lastCharStep = -1;
let lastCharPhase = null;

function applyServerState(serverState) {
  if (!serverState || !serverState.players) return;
  const wasMyTurn = state && state.currentPlayer === seatIdx;
  const prevHandIds = lastHandIds;
  state = serverState;
  // Sort this client's own hand for display; server doesn't guarantee order.
  if (state.players[seatIdx]?.hand) sortHand(state.players[seatIdx].hand);

  // Detect newly-drawn tiles on the viewer's hand so we can highlight +
  // announce them. Any id in the new hand that wasn't in the previous is
  // "new" — for a draw, exactly one; for a claim, it's on exposed, not hand.
  const currentHandIds = new Set(state.players[seatIdx]?.hand?.map((t) => t.id) ?? []);
  const newlyDrawn = [];
  for (const id of currentHandIds) if (!prevHandIds.has(id)) newlyDrawn.push(id);
  lastHandIds = currentHandIds;
  if (newlyDrawn.length === 1 && prevHandIds.size > 0) {
    // Only announce on a genuine delta (not on initial hydrate).
    const t = state.players[seatIdx].hand.find((x) => x.id === newlyDrawn[0]);
    if (t) {
      ui.recentDrawId = t.id;
      ui.recentDrawAt = Date.now();
      setCommentary(`You drew: ${tileName(t)}. Pick a tile to discard.`);
      setTimeout(() => {
        if (ui.recentDrawId === t.id) {
          ui.recentDrawId = null;
          render();
        }
      }, 3000);
    }
  }

  // Drive the charleston overlay if we're in that phase.
  if (state.phase === 'charleston') {
    // If the step just advanced, clear any stale picks so the picker
    // re-opens fresh.
    if (state.charStep !== lastCharStep || (state.charPhase ?? null) !== lastCharPhase) {
      ui.charPicked = [];
      lastCharStep = state.charStep;
      lastCharPhase = state.charPhase ?? null;
    }
    renderCharlestonOverlay();
  } else {
    document.getElementById('charOverlay')?.classList.remove('show');
    if (state.phase === 'play' && lastCharStep >= 0) {
      // Just left charleston — let the player know.
      setCommentary('Charleston complete! East discards first.');
    }
    lastCharStep = -1;
  }

  render();
  // Ding when it just became my turn.
  const isMyTurnNow = state.currentPlayer === seatIdx && state.phase === 'play';
  if (isMyTurnNow && state.currentPlayer !== lastCurrentPlayer) {
    dingYourTurn();
    if (!wasMyTurn && !ui.recentDrawId) {
      // Don't overwrite the "You drew: X" message if it's already showing.
      setCommentary('Your turn! Tap "Draw" to draw a tile.');
    }
  }
  lastCurrentPlayer = state.currentPlayer;
  if (state.phase === 'done' && state.winner) announceEnd();
}

function render() {
  if (!state) return;
  // 🃏 Swap button — same rule as local play; the server validates the move.
  ui.swapCount =
    state.phase === 'play' && state.currentPlayer === seatIdx && state.hasDrawn && !busy
      ? jokerExchangeOptions(state, seatIdx).length
      : 0;
  renderGame(state, ui, canDeclare, seatIdx);
}

function canDeclare() {
  if (!state || state.phase !== 'play') return false;
  if (state.currentPlayer !== seatIdx) return false;
  if (!state.hasDrawn) return false;
  if (busy) return false;
  const p = state.players[seatIdx];
  let total = p.hand.length;
  for (const g of p.exposed) total += g.tiles.length;
  return total >= 14;
}

async function submit(action, payload) {
  if (busy) return;
  busy = true;
  render();
  try {
    await submitMove(gameId, action, payload ?? {});
    // Belt + suspenders: force-refresh state from the DB after the server
    // has accepted the move. Realtime should also fire, but if it's slow
    // or silent (new publishable-key format + mobile quirks), this ensures
    // the UI reflects the server truth within one round-trip.
    try {
      const row = await loadGameRow(gameId);
      if (row) applyServerState(row.state);
    } catch {
      /* ignore — realtime will likely catch up */
    }
  } catch (e) {
    setCommentary('Server rejected the move: ' + (e.message || 'unknown'));
  } finally {
    busy = false;
    render();
  }
}

export function selectTileAt(idx) {
  if (!state || state.phase !== 'play') return;
  ui.selectedTile = ui.selectedTile === idx ? -1 : idx;
  render();
}

export function playerDraw() {
  if (!state || state.currentPlayer !== seatIdx || state.hasDrawn) return;
  submit('draw');
}

export function playerDiscard() {
  if (!state || ui.selectedTile < 0 || !state.hasDrawn) return;
  const tileId = state.players[seatIdx].hand[ui.selectedTile].id;
  ui.selectedTile = -1;
  submit('discard', { tile_id: tileId });
}

export function declareMahjong() {
  if (!canDeclare()) return;
  submit('mahjong');
}

// 🃏 Swap button: list every legal joker exchange; the server applies it.
export function showSwapOptions() {
  if (!state || busy) return;
  const options = jokerExchangeOptions(state, seatIdx);
  if (!options.length) return;
  const el = document.getElementById('claimPrompt');
  const pass = document.getElementById('claimPass');
  el.classList.add('show');
  document.getElementById('claimMsg').textContent =
    'Swap a tile from your hand for an exposed Joker:';
  const actions = document.getElementById('claimActions');
  actions.innerHTML = '';
  for (const opt of options) {
    const owner = state.players[opt.ownerIdx];
    const group = owner.exposed[opt.groupIdx];
    const natural = group.tiles.find((t) => t.type !== 'joker');
    const whose = opt.ownerIdx === seatIdx ? 'your' : owner.name + "'s";
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-claim';
    btn.textContent = `${tileName(natural)} → 🃏 (${whose} ${opt.kind})`;
    btn.onclick = () => {
      el.classList.remove('show');
      pass.textContent = 'Pass';
      submit('joker_swap', { owner_idx: opt.ownerIdx, group_idx: opt.groupIdx });
      setCommentary(`You swapped your ${tileName(natural)} for the Joker in ${whose} ${opt.kind}!`);
    };
    actions.appendChild(btn);
  }
  pass.textContent = 'Cancel';
  pass.onclick = () => {
    el.classList.remove('show');
    pass.textContent = 'Pass';
  };
}

export function showHint() {
  if (!state) return;
  const hand = state.players[seatIdx].hand;
  const ranked = rankTargets(hand, state.players[seatIdx].exposed.length > 0).slice(0, 3);
  if (ranked.length === 0) {
    ui.hintPattern = null;
    ui.hintIdx = -1;
    setCommentary('💡 Hint: keep one or two suits. Discard winds and isolated tiles.');
    render();
    return;
  }
  ui.hintIdx = (ui.hintIdx + 1) % (ranked.length + 1);
  if (ui.hintIdx === ranked.length) {
    ui.hintPattern = null;
    setCommentary('💡 Hint cleared. Click again to cycle top targets.');
  } else {
    const t = ranked[ui.hintIdx];
    ui.hintPattern = t.hand;
    setCommentary(`💡 Target ${ui.hintIdx + 1}/${ranked.length}: ${t.section} — ${t.label} (${t.value}${t.concealed ? ' C' : ''}). Need: ${targetNeeds(t, hand)}.`);
  }
  render();
}

function announceEnd() {
  const w = state.winner;
  const el = document.getElementById('winOverlay');
  const msg = document.getElementById('winMsg');
  if (w && w.seat) {
    msg.textContent = `${w.name} (${w.seat}) wins!`;
  } else {
    msg.textContent = 'Wall game — nobody won this round.';
  }
  el.classList.add('show');
}

export function exitOnline() {
  if (unsub) unsub();
  unsub = null;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  deactivateChat();
  const chatBtn = document.getElementById('btnChatToggle');
  if (chatBtn) chatBtn.style.display = 'none';
  state = null;
  gameId = '';
  mySeat = '';
  seatIdx = -1;
  if (onExitCallback) onExitCallback();
}

// ---------- Charleston (online) ----------
// Three server-driven phases: 'pass' rounds, a 'vote' on the optional second
// charleston (unanimous to continue), then the 'courtesy' exchange (0–3
// tiles with the player across; the pair exchanges the lesser offer).
function waitingNames(pendingMap) {
  return state.players
    .filter((p) => !p.isAI && pendingMap[p.seat] == null && p.seat !== mySeat)
    .map((p) => p.name);
}

function showWaiting(pendingMap, doneText) {
  const waitingOn = waitingNames(pendingMap);
  document.getElementById('charDesc').textContent =
    waitingOn.length ? `Waiting on: ${waitingOn.join(', ')}…` : 'Processing…';
  document.getElementById('charTiles').innerHTML = '';
  document.getElementById('charCount').textContent = doneText;
  const btn = document.getElementById('charBtn');
  btn.disabled = true;
  btn.textContent = 'Waiting…';
}

function renderCharlestonOverlay() {
  const ov = document.getElementById('charOverlay');
  if (!ov) return;
  ov.classList.add('show');
  const skipBtn = document.getElementById('charSkipBtn');
  skipBtn.style.display = 'none';

  if (state.charPhase === 'vote') {
    document.getElementById('charTitle').textContent = 'Second Charleston?';
    const votes = state.charlestonVotes || {};
    if (votes[mySeat] != null) {
      showWaiting(votes, '✓ Your vote is in');
      return;
    }
    document.getElementById('charDesc').textContent =
      'Keep passing (3 more rounds)? All four players must agree — any player can stop it.';
    document.getElementById('charTiles').innerHTML = '';
    document.getElementById('charCount').textContent = '';
    const btn = document.getElementById('charBtn');
    btn.disabled = busy;
    btn.textContent = 'Yes — Keep Passing';
    skipBtn.style.display = '';
    skipBtn.textContent = 'Stop Here';
    return;
  }

  if (state.charPhase === 'courtesy') {
    document.getElementById('charTitle').textContent = 'Courtesy Pass';
    const pending = state.courtesyPending || {};
    if (pending[mySeat] != null) {
      showWaiting(pending, '✓ Your offer is in');
      return;
    }
    const acrossSeat = { East: 'West', West: 'East', South: 'North', North: 'South' }[mySeat];
    const across = state.players.find((p) => p.seat === acrossSeat);
    document.getElementById('charDesc').textContent =
      `Offer 0–3 tiles to ${across ? across.name : acrossSeat} (across the table). ` +
      "You'll exchange the SMALLER of your two offers.";
    renderCharTiles(state, ui.charPicked, seatIdx);
    document.getElementById('charCount').textContent =
      ui.charPicked.length + ' / up to 3 selected';
    const btn = document.getElementById('charBtn');
    btn.disabled = busy;
    btn.textContent =
      ui.charPicked.length === 0
        ? 'Offer No Tiles'
        : `Offer ${ui.charPicked.length} Tile${ui.charPicked.length > 1 ? 's' : ''}`;
    return;
  }

  const pending = state.charlestonPending || {};
  const mySubmitted = !!pending[mySeat];
  document.getElementById('charTitle').textContent =
    `Charleston — Pass ${state.charStep + 1} of 6`;

  if (mySubmitted) {
    showWaiting(pending, '✓ Your tiles are passed');
  } else {
    document.getElementById('charDesc').textContent = CHAR_LABELS[state.charStep];
    renderCharTiles(state, ui.charPicked, seatIdx);
    const btn = document.getElementById('charBtn');
    btn.disabled = ui.charPicked.length !== 3;
    btn.textContent = busy ? 'Sending…' : 'Pass Tiles';
  }
}

export function charPickTile(idx) {
  if (!state || state.phase !== 'charleston') return;
  if (state.charPhase === 'vote') return;
  if (state.charPhase === 'courtesy') {
    if (state.courtesyPending?.[mySeat] != null) return; // already offered
  } else if (state.charlestonPending?.[mySeat]) {
    return; // already submitted this round
  }
  const pos = ui.charPicked.indexOf(idx);
  if (pos >= 0) ui.charPicked.splice(pos, 1);
  else if (ui.charPicked.length < 3) ui.charPicked.push(idx);
  renderCharlestonOverlay();
}

export async function charPass() {
  if (!state || state.phase !== 'charleston') return;
  if (state.charPhase === 'vote') {
    if (state.charlestonVotes?.[mySeat] != null) return;
    await submit('charleston_vote', { continue: true });
    return;
  }
  if (state.charPhase === 'courtesy') {
    if (state.courtesyPending?.[mySeat] != null) return;
    const tileIds = ui.charPicked.map((i) => state.players[seatIdx].hand[i].id);
    ui.charPicked = [];
    await submit('charleston_courtesy', { tile_ids: tileIds });
    return;
  }
  if (ui.charPicked.length !== 3) return;
  if (state.charlestonPending?.[mySeat]) return;
  const tileIds = ui.charPicked.map((i) => state.players[seatIdx].hand[i].id);
  ui.charPicked = [];
  await submit('charleston_pass', { tile_ids: tileIds });
}

export async function charSkip() {
  if (!state || state.phase !== 'charleston' || state.charPhase !== 'vote') return;
  if (state.charlestonVotes?.[mySeat] != null) return;
  await submit('charleston_vote', { continue: false });
}

export function hideOnlineWin() {
  document.getElementById('winOverlay').classList.remove('show');
}
